import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableCell,
  TableHead,
  TableBody,
  TableRow,
  Button,
  Alert,
  FormControl,
  TextField,
} from "@mui/material";
import { useMemo, useState } from "react";
import { ClipboardDisplayGroup, ClipboardDisplayGroupItemType } from "./ImportUtils";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  fetchCaseDisplayGroups,
  selectCaseDisplayGroups,
  updateCaseDisplayGroupWithItems,
} from "redux/slices/lensEditor";
import { useClipboard } from "helpers/hooks/useClipboard";
import { useSnackbar } from "notistack";

export function ImportCaseDisplayGroupsDialog({ onClose, lensId }: { onClose: () => void; lensId: number }) {
  const [contents, setContents] = useState("");
  const [importing, setImporting] = useState(false);
  const clipboardCaseDisplayGroups: ClipboardDisplayGroup[] = useMemo(
    () => (contents ? JSON.parse(contents) : []),
    [contents]
  );
  const caseDisplayGroups = useAppSelector(selectCaseDisplayGroups);
  const caseDisplayGroupNameSet = new Set(caseDisplayGroups.map((cdg) => cdg.name));
  const [conflictingNames, setConflictingNames] = useState<string[]>([]);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  useClipboard(setContents);

  const onImport = async () => {
    const foundNames = clipboardCaseDisplayGroups
      .filter((cdg) => caseDisplayGroupNameSet.has(cdg.name))
      .map((cdg) => cdg.name);
    setConflictingNames(foundNames);

    if (foundNames.length === 0) {
      try {
        setImporting(true);
        const promises = clipboardCaseDisplayGroups.map((ccdg) => {
          return dispatch(
            updateCaseDisplayGroupWithItems({
              lensId,
              rows: ccdg.rows,
              name: ccdg.name,
              columns: ccdg.columns,
              rowDescriptions: ccdg.rowDescriptions,
              columnDescriptions: ccdg.columnDescriptions,
              type: ccdg.type,
              description: ccdg.description,
              items: ccdg.items.map((item, index) => ({
                lensId,
                rank: index,
                caseCalcPropertyKey: item.type === ClipboardDisplayGroupItemType.calc ? item.key : undefined,
                caseCustomPropertyKey: item.type === ClipboardDisplayGroupItemType.property ? item.key : undefined,
                rowIndex: item.rowIndex,
                colIndex: item.colIndex,
              })),
            })
          ).unwrap();
        });
        await Promise.all(promises).then(() => dispatch(fetchCaseDisplayGroups({ lensId })).unwrap());
        enqueueSnackbar("Case Display Groups imported", { variant: "success" });
        onClose();
      } catch (e) {
        console.error(e);
        enqueueSnackbar("Unable to import case dispay groups", { variant: "error" });
      } finally {
        setImporting(false);
      }
    }
  };

  return (
    <Dialog open={true} maxWidth="lg" fullWidth>
      <DialogTitle>Import Case Display Groups</DialogTitle>
      <DialogContent>
        {conflictingNames.length > 0 && (
          <Alert severity="error">The following group names already exist: {conflictingNames.join(", ")}</Alert>
        )}
        {!contents && (
          <FormControl fullWidth>
            <TextField
              value={contents}
              onChange={(evt) => setContents(evt.target.value)}
              label="Paste Case Display Groups"
            />
          </FormControl>
        )}
        {clipboardCaseDisplayGroups.length > 0 && (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clipboardCaseDisplayGroups.map((ccdg) => (
                <TableRow key={ccdg.name}>
                  <TableCell>{ccdg.name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={importing} sx={{ mr: 2 }} variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={importing} variant="contained" onClick={onImport}>
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}
